/**
 * Wildlife scoring card data.
 *
 * Phase 1 ships a representative subset that exercises every `WildlifeRule`
 * kind and that covers the cards required by sample scenarios A–E. The full
 * 21 + 15 + KS Promo catalog lands with P2 fixtures (TDD).
 *
 * Numerical values follow the rules-spec encoding sketch
 * (`docs/task-log/20260505-cascadia-online/02-rules-spec.md` §7.1.1+).
 */

import type { WildlifeScoringCardDef } from "../types/card";

export const WILDLIFE_SCORING_CARDS: readonly WildlifeScoringCardDef[] = [
  // Bear — base
  {
    cardId: "bear_A",
    wildlife: "bear",
    variantId: "A",
    setLabel: "base",
    rule: {
      kind: "pointsPerGroupOfSize",
      mode: "exact",
      size: 2,
      points: 4,
    },
  },
  {
    cardId: "bear_B",
    wildlife: "bear",
    variantId: "B",
    setLabel: "base",
    rule: {
      kind: "pointsPerGroupOfSize",
      mode: "exact",
      size: 3,
      points: 10,
    },
  },
  {
    cardId: "bear_C",
    wildlife: "bear",
    variantId: "C",
    setLabel: "base",
    rule: {
      kind: "pointsPerGroupOfSize",
      mode: "exact",
      size: 1,
      points: 2,
      cap: 5,
    },
  },
  {
    cardId: "bear_D",
    wildlife: "bear",
    variantId: "D",
    setLabel: "base",
    rule: {
      kind: "pointsPerPairWithinDistance",
      distance: 1,
      pointsPerPair: 5,
    },
  },

  // Hawk — base
  {
    cardId: "hawk_A",
    wildlife: "hawk",
    variantId: "A",
    setLabel: "base",
    rule: {
      kind: "pointsPerSurroundingTileCount",
      counts: { 0: 2 },
    },
  },
  {
    cardId: "hawk_C",
    wildlife: "hawk",
    variantId: "C",
    setLabel: "base",
    rule: {
      kind: "pointsPerLine",
      minLength: 2,
      pointsPerStraight: { 2: 3, 3: 6, 4: 9, 5: 12 },
    },
  },

  // Elk — base
  {
    cardId: "elk_A",
    wildlife: "elk",
    variantId: "A",
    setLabel: "base",
    rule: {
      kind: "pointsPerLine",
      minLength: 1,
      pointsPerStraight: { 1: 2, 2: 5, 3: 9, 4: 13 },
    },
  },

  // Salmon — base
  {
    cardId: "salmon_A",
    wildlife: "salmon",
    variantId: "A",
    setLabel: "base",
    rule: {
      kind: "pointsPerLine",
      minLength: 1,
      pointsPerStraight: { 1: 2, 2: 4, 3: 7, 4: 11 },
    },
  },
  {
    cardId: "salmon_D",
    wildlife: "salmon",
    variantId: "D",
    setLabel: "base",
    rule: {
      kind: "pointsPerLine",
      minLength: 1,
      pointsPerStraight: { 1: 2, 2: 4, 3: 8, 4: 12, 5: 17, 6: 22 },
    },
  },

  // Fox — base
  {
    cardId: "fox_A",
    wildlife: "fox",
    variantId: "A",
    setLabel: "base",
    rule: {
      kind: "pointsPerAdjacentDistinctSpeciesCount",
      counts: { 1: 1, 2: 2, 3: 3, 4: 4 },
    },
  },

  // Fox — Cascadia KS Promo (used by sample scenario E)
  {
    cardId: "fox_F",
    wildlife: "fox",
    variantId: "F",
    setLabel: "ks",
    rule: {
      kind: "pointsPerAdjacentDistinctSpeciesCount",
      counts: { 2: 3 },
    },
  },
];

export const WILDLIFE_SCORING_CARDS_BY_ID: Readonly<
  Record<string, WildlifeScoringCardDef>
> = Object.freeze(
  Object.fromEntries(WILDLIFE_SCORING_CARDS.map((card) => [card.cardId, card])),
);
